import { z } from "zod";
import { workCoverSchema } from "./workCover.ts";
import { canonicalIdSchema } from "./content.ts";

export const sourceLanguageSchema = z.enum(["zh-CN", "en"]);
export const publicLanguageSchema = z.enum(["zh-CN", "en"]);
export const workStatusSchema = z.enum(["ongoing", "paused", "completed"]);
export const releaseKindSchema = z.enum(["regular", "extra"]);
export const workSourceKindSchema = z.enum(["original", "crosspost", "repost"]);
export const workSourcePlatformSchema = z.enum([
  "ao3",
  "pixiv",
  "lofter",
  "other"
]);
export const emptyWorkSourceProvenance = {
  kind: "original",
  platform: null,
  url: null,
  authorName: null,
  declaredAt: null,
  rightsAttestedAt: null
} as const;
const workSourceUrlSchema = z
  .string()
  .trim()
  .url()
  .max(2048)
  .refine((value) => {
    try {
      const protocol = new URL(value).protocol;
      return protocol === "http:" || protocol === "https:";
    } catch {
      return false;
    }
  }, "Source URL must use HTTP or HTTPS.");
export const workSourceDeclarationSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("original") }).strict(),
  z
    .object({
      kind: z.enum(["crosspost", "repost"]),
      platform: workSourcePlatformSchema,
      url: workSourceUrlSchema,
      authorName: z.string().trim().min(1).max(160).optional(),
      rightsAttested: z.literal(true)
    })
    .strict()
]);
export const workSourceProvenanceSchema = z
  .object({
    kind: workSourceKindSchema,
    platform: workSourcePlatformSchema.nullable(),
    url: workSourceUrlSchema.nullable(),
    authorName: z.string().trim().min(1).max(160).nullable(),
    declaredAt: z.iso.datetime({ offset: true }).nullable(),
    rightsAttestedAt: z.iso.datetime({ offset: true }).nullable()
  })
  .strict();
export const creatorSummaryCapabilitySchema = z
  .object({
    version: z.literal(2),
    generatedWorkSynopsis: z.literal("supported"),
    creatorWrittenSummaryDraft: z.literal("retired")
  })
  .strict();

const chapterDraftInputSchema = z
  .object({
    clientDraftId: z.string().trim().min(1).max(120),
    chapterIndex: z.number().int().nonnegative(),
    title: z.string().trim().min(1).max(240),
    sourceBody: z.string().trim().min(1).max(200_000),
    summaryDraft: z.string().trim().min(1).max(1_000).optional()
  })
  .strict();

export const workCreateRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(240),
    sourceLanguage: sourceLanguageSchema,
    status: workStatusSchema,
    fandomId: canonicalIdSchema,
    shipIds: z.array(canonicalIdSchema).max(8),
    tagIds: z.array(canonicalIdSchema).max(24),
    source: workSourceDeclarationSchema.default({ kind: "original" })
  })
  .strict();

export const workCreateResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    title: z.string().min(1),
    sourceLanguage: sourceLanguageSchema,
    status: workStatusSchema,
    source: workSourceProvenanceSchema.default(emptyWorkSourceProvenance),
    releaseCount: z.literal(0)
  })
  .strict();

export const releasePublishRequestSchema = z
  .object({
    idempotencyKey: z.string().trim().min(1).max(120),
    releaseDraftId: canonicalIdSchema
  })
  .strict();

export const releaseDraftSaveRequestSchema = z
  .object({
    kind: releaseKindSchema,
    chapters: z.array(chapterDraftInputSchema).min(1).max(20)
  })
  .strict()
  .refine(
    ({ chapters }) =>
      new Set(chapters.map(({ clientDraftId }) => clientDraftId)).size ===
        chapters.length &&
      new Set(chapters.map(({ chapterIndex }) => chapterIndex)).size ===
        chapters.length,
    { message: "Chapter IDs and indexes must be unique", path: ["chapters"] }
  );

export const releaseDraftResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    releaseDraftId: canonicalIdSchema,
    kind: releaseKindSchema,
    sourceLanguage: sourceLanguageSchema,
    chapters: z.array(
      z
        .object({
          id: canonicalIdSchema,
          clientDraftId: z.string().min(1),
          chapterIndex: z.number().int().nonnegative(),
          title: z.string().min(1),
          sourceFingerprint: z.string().min(8),
          summaryDraft: z.string().nullable(),
          approvedSummary: z.string().nullable(),
          summaryApproved: z.boolean()
        })
        .strict()
    )
  })
  .strict();

export const chapterSummaryApprovalRequestSchema = z
  .object({
    expectedSourceFingerprint: z.string().min(8).max(128),
    approvedSummary: z.string().trim().min(1).max(1_000)
  })
  .strict();

export const chapterSummaryApprovalResponseSchema = z
  .object({
    chapterDraftId: canonicalIdSchema,
    approvedSummary: z.string().min(1),
    summaryApproved: z.literal(true)
  })
  .strict();

export const releasePublishResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    releaseId: canonicalIdSchema,
    releaseVersionId: canonicalIdSchema,
    releaseIndex: z.number().int().positive(),
    kind: releaseKindSchema,
    chapterIds: z.array(canonicalIdSchema).min(1),
    sourceLanguage: sourceLanguageSchema,
    targetLanguage: sourceLanguageSchema,
    translationStatus: z.literal("pending"),
    previewSourceUnitLimit: z.literal(1_000).optional(),
    publishedAt: z.iso.datetime({ offset: true })
  })
  .strict();

export const workStatusMutationRequestSchema = z
  .object({ status: workStatusSchema })
  .strict();

export const workStatusMutationResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    status: workStatusSchema
  })
  .strict();

export const fullTranslationIntentRequestSchema = z
  .object({ targetLanguage: sourceLanguageSchema })
  .strict();

export const fullTranslationIntentResponseSchema = z
  .object({
    intentId: canonicalIdSchema,
    workId: canonicalIdSchema,
    targetLanguage: sourceLanguageSchema,
    durable: z.literal(true)
  })
  .strict();

export const releaseReaderResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    title: z.string().min(1),
    sourceLanguage: sourceLanguageSchema,
    targetLanguage: sourceLanguageSchema,
    status: workStatusSchema,
    cover: workCoverSchema,
    source: workSourceProvenanceSchema.default(emptyWorkSourceProvenance),
    ships: z.array(
      z
        .object({
          id: canonicalIdSchema,
          label: z.string().min(1)
        })
        .strict()
    ),
    engagement: z
      .object({
        bookmarked: z.boolean(),
        reaction: z.literal("kudos").nullable(),
        readingProgress: z
          .object({
            releaseVersionId: canonicalIdSchema,
            chapterId: canonicalIdSchema,
            segmentId: canonicalIdSchema,
            segmentIndex: z.number().int().nonnegative(),
            completed: z.boolean()
          })
          .strict()
          .nullable()
      })
      .strict(),
    releases: z.array(
      z
        .object({
          id: canonicalIdSchema,
          releaseIndex: z.number().int().positive(),
          kind: releaseKindSchema,
          translationStatus: z.enum([
            "source",
            "pending",
            "processing",
            "published",
            "failed"
          ]),
          translationScope: z.enum(["preview", "full"]),
          chapters: z.array(
            z
              .object({
                id: canonicalIdSchema,
                chapterIndex: z.number().int().nonnegative(),
                title: z.string().min(1),
                summary: z.string().min(1),
                segments: z.array(
                  z
                    .object({
                      id: canonicalIdSchema,
                      segmentIndex: z.number().int().nonnegative(),
                      sourceText: z.string().min(1),
                      displayText: z.string().min(1),
                      translated: z.boolean()
                    })
                    .strict()
                )
              })
              .strict()
          )
        })
        .strict()
    )
  })
  .strict();

export const publicReleaseReaderResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    title: z.string().min(1),
    sourceLanguage: publicLanguageSchema,
    requestedLanguage: publicLanguageSchema,
    status: workStatusSchema,
    source: workSourceProvenanceSchema.default(emptyWorkSourceProvenance),
    ships: z.array(
      z.object({ id: canonicalIdSchema, label: z.string().min(1) }).strict()
    ),
    releases: z.array(
      z
        .object({
          id: canonicalIdSchema,
          releaseVersionId: canonicalIdSchema,
          releaseIndex: z.number().int().positive(),
          kind: releaseKindSchema,
          translationStatus: z.enum([
            "source",
            "pending",
            "processing",
            "published",
            "failed",
            "unavailable"
          ]),
          chapters: z.array(
            z
              .object({
                id: canonicalIdSchema,
                chapterIndex: z.number().int().nonnegative(),
                title: z.string().min(1),
                summary: z.string().min(1),
                segments: z.array(
                  z
                    .object({
                      id: canonicalIdSchema,
                      segmentIndex: z.number().int().nonnegative(),
                      sourceText: z.string().min(1),
                      displayText: z.string().min(1),
                      translated: z.boolean()
                    })
                    .strict()
                )
              })
              .strict()
          )
        })
        .strict()
    )
  })
  .strict();

export type PublicReleaseReaderResponse = z.infer<
  typeof publicReleaseReaderResponseSchema
>;

export const workBookmarkMutationResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    bookmarked: z.boolean()
  })
  .strict();

export const workReactionMutationResponseSchema = z
  .object({
    workId: canonicalIdSchema,
    reaction: z.literal("kudos").nullable()
  })
  .strict();

export const workProgressMutationRequestSchema = z
  .object({
    chapterId: canonicalIdSchema,
    segmentId: canonicalIdSchema,
    segmentIndex: z.number().int().nonnegative(),
    completed: z.boolean()
  })
  .strict();

export const workProgressMutationResponseSchema =
  workProgressMutationRequestSchema
    .extend({
      workId: canonicalIdSchema,
      releaseVersionId: canonicalIdSchema
    })
    .strict();

const translatedTextSchema = z
  .object({
    original: z.string(),
    originalLanguage: sourceLanguageSchema,
    display: z.string(),
    displayLanguage: sourceLanguageSchema,
    translated: z.boolean()
  })
  .strict();

export const commentCreateRequestSchema = z
  .object({
    body: z.string().trim().min(1).max(5_000),
    sourceLanguage: sourceLanguageSchema,
    parentCommentId: canonicalIdSchema.nullable().optional()
  })
  .strict();

export const commentSchema = z
  .object({
    id: canonicalIdSchema,
    workId: canonicalIdSchema,
    parentCommentId: canonicalIdSchema.nullable(),
    author: z
      .object({
        id: canonicalIdSchema,
        displayName: z.string().min(1)
      })
      .strict(),
    body: translatedTextSchema,
    createdAt: z.iso.datetime({ offset: true })
  })
  .strict();

export const commentListResponseSchema = z
  .object({ items: z.array(commentSchema) })
  .strict();

export const commentCreateResponseSchema = z
  .object({ comment: commentSchema })
  .strict();

export type SourceLanguage = z.infer<typeof sourceLanguageSchema>;
export type WorkCreateRequest = z.infer<typeof workCreateRequestSchema>;
export type WorkSourceDeclaration = z.infer<typeof workSourceDeclarationSchema>;
export type WorkSourceProvenance = z.infer<typeof workSourceProvenanceSchema>;
export type ReleasePublishRequest = z.infer<typeof releasePublishRequestSchema>;
export type ReleaseDraftSaveRequest = z.infer<
  typeof releaseDraftSaveRequestSchema
>;
export type ChapterSummaryApprovalRequest = z.infer<
  typeof chapterSummaryApprovalRequestSchema
>;
export type CommentCreateRequest = z.infer<typeof commentCreateRequestSchema>;
export type WorkReactionMutationResponse = z.infer<
  typeof workReactionMutationResponseSchema
>;
